import { useState } from "react";
import axios from "axios";
import { Link2, Loader2, Plus, Search } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { verkendItem } from "@/lib/koppelcatalogus";

const INLOG_UITLEG = {
  geen: "Inlogmanier: zonder inlog",
  oauth: "Inlogmanier: inloggen bij de aanbieder",
  sleutel: "Inlogmanier: met een sleutel",
};

/**
 * Plan b (GO Frank 5 nov 2026): "Connector toevoegen" in Ontdekken. Plak een adres; NovaMund verkent de server
 * (`POST /mcp-servers/verken`) en SLAAT NIETS OP. Je ziet de inlogmanier en de gereedschappen. Pas de knop Koppelen
 * koppelt, via `onKoppel`, dezelfde weg als een kaart uit de catalogus.
 */
export default function ConnectorToevoegen({ onKoppel, bezig, token, onToken }) {
  const { t } = useTaal();
  const [open, setOpen] = useState(false);
  const [adres, setAdres] = useState("");
  const [verkennen, setVerkennen] = useState(false);
  const [fout, setFout] = useState("");
  const [item, setItem] = useState(null);

  const verken = async () => {
    setFout("");
    setItem(null);
    setVerkennen(true);
    try {
      const r = await axios.post(`${API}/mcp-servers/verken`, { adres: adres.trim() });
      const gevonden = verkendItem(r?.data);
      if (!gevonden) setFout(t("Dat lukte niet. Er is niets gewijzigd."));
      setItem(gevonden);
    } catch (e) {
      setFout(e.response?.data?.detail || t("Dat lukte niet. Er is niets gewijzigd."));
    } finally {
      setVerkennen(false);
    }
  };

  if (!open) {
    return (
      <Button size="sm" variant="outline" className="mb-3" onClick={() => setOpen(true)} data-testid="connector-toevoegen">
        <Plus className="h-3 w-3 mr-1" />{t("Connector toevoegen")}
      </Button>
    );
  }
  return (
    <section className="mb-3 border rounded-md p-3 space-y-2" data-testid="connector-toevoegen-paneel">
      <p className="text-xs text-muted-foreground">
        {t("Plak het adres van een koppelserver. NovaMund kijkt wat de server is en slaat niets op; koppelen doe je daarna zelf.")}
      </p>
      <div className="flex gap-2">
        <Input value={adres} onChange={(e) => { setAdres(e.target.value); setItem(null); }} autoComplete="off"
               placeholder="https://" aria-label={t("Adres van de koppelserver")} className="h-8 text-xs"
               data-testid="connector-adres" />
        <Button size="sm" variant="outline" disabled={!adres.trim() || verkennen} onClick={verken}
                data-testid="connector-verken">
          {verkennen ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}
          {t("Verkennen")}
        </Button>
      </div>
      {fout && <p className="text-xs text-[hsl(var(--destructive))]" data-testid="connector-fout">{fout}</p>}
      {item && (
        <div className="space-y-1" data-testid="connector-uitslag">
          <p className="text-sm font-medium">{item.naam || item.adres}</p>
          <p className="text-xs" data-testid="connector-inloggen">{t(INLOG_UITLEG[item.inloggen])}</p>
          {item.melding && <p className="text-xs text-muted-foreground" data-testid="connector-melding">{t(item.melding)}</p>}
          {item.gereedschappen.length > 0 && (
            <ul className="text-xs list-disc pl-4" data-testid="connector-gereedschappen">
              {item.gereedschappen.map((g) => (
                <li key={g.naam}><span className="font-mono">{g.naam}</span>{g.omschrijving ? `: ${g.omschrijving}` : ""}</li>
              ))}
            </ul>
          )}
          {item.sleutelNodig && (
            <Input type="password" autoComplete="off" value={token || ""} onChange={(e) => onToken(e.target.value)}
                   placeholder={t("Je token")} aria-label={t("Token voor {naam}", { naam: item.naam || item.adres })}
                   className="h-8 text-xs" data-testid="connector-token" />
          )}
          <Button size="sm" variant="outline" disabled={!!bezig} onClick={() => onKoppel(item)} data-testid="connector-koppel">
            {bezig === item.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Link2 className="h-3 w-3 mr-1" />}
            {t("Koppelen")}
          </Button>
        </div>
      )}
    </section>
  );
}
